/**
 * CSV export, once. `downloadCsv` turns rows (the table's own database columns, as the backend's export
 * returns them) into a file; `exportEntityCsv` is what a list's "Export" menu item calls: the rows come from
 * the backend for the list's CURRENT query -- the selected rows, or every match when nothing is selected --
 * never from the pages loaded in the table.
 */

const cell = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  const text = typeof value === 'object' ? JSON.stringify(value) : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export function rowsToCsv(rows: Array<Record<string, unknown>>): string {
  if (rows.length === 0) return '';
  const columns = Object.keys(rows[0]);
  return [columns.join(','), ...rows.map((row) => columns.map((column) => cell(row[column])).join(','))].join('\n');
}

export function downloadCsv(filename: string, rows: Array<Record<string, unknown>>): void {
  const blob = new Blob([rowsToCsv(rows)], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.setAttribute('href', URL.createObjectURL(blob));
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * @param exportRows `useEntityList(...).exportRows`
 * @param selectedIds rows selected in the table; empty = export everything the current search / filters / project match
 * @param basename   file name without date / extension, e.g. 'purchase-orders'; `label` is its human name for the alerts
 */
export async function exportEntityCsv(
  exportRows: (ids?: string[]) => Promise<Array<Record<string, unknown>>>,
  selectedIds: string[],
  basename: string,
  label: string
): Promise<void> {
  try {
    const rows = await exportRows(selectedIds.length > 0 ? selectedIds : undefined);
    if (rows.length === 0) {
      alert('No data found to export.');
      return;
    }
    downloadCsv(`${basename}-${new Date().toISOString().split('T')[0]}.csv`, rows);
  } catch (error) {
    console.error(`Error exporting ${label}:`, error);
    alert(`Error exporting ${label}. Please try again.`);
  }
}
